/**
 * Turns the gateway's reading blocks into subtitle lines, client-side.
 *
 * The STT gateway returns one cue per speech chunk — up to 30 seconds and 414
 * characters. That reads fine as a transcript and is unusable as subtitles.
 * Everything here is post-processing of a result we already paid for: same
 * request, same credit, same words — only the cue boundaries change.
 *
 * Three steps per block:
 *   1. cut the text on the strongest available boundary (sentence → clause →
 *      dash → comma → words), recursively, until every piece fits the norm;
 *   2. place the timings — proportional to text length, then refined against
 *      the loudness envelope when the audio is at hand;
 *   3. merge neighbours back while the merge still fits the norm.
 *
 * A block that already fits the norm is returned untouched, so a transcript
 * that was fine to begin with comes out byte-identical.
 */
import type { Cue } from './subtitleConvert'

/** Per-frame RMS of the audio, from its start. */
export interface LoudnessEnvelope {
  /** Seconds per frame (HOP). */
  hop: number
  rms: Float32Array
}

// Subtitle norm: 2 lines × 42 characters, 7 seconds, 21 characters/second.
export const MAX_CHARS = 84
export const MAX_DUR = 7.0
export const MIN_DUR = 0.9
export const MAX_CPS = 21
/** Envelope grid — also the resolution of every boundary we can pick. */
export const HOP = 0.01

// Weights of the two forces deciding where a boundary lands: cut where it is
// quiet (W_ENERGY) versus stay where the text says it should be (W_PRIOR).
// Tuned by sweep on ru.mp3 — median error 200 ms against word-level reference.
const W_ENERGY = 3.0
const W_PRIOR = 2.2
/** Weight of matching a cut to a pause of its own size. */
const W_MATCH = 5.0
/** Anchoring: how far a sentence end may drift from its proportional estimate
 * before the pause it claims stops being worth it, and what an unclaimed
 * sentence end costs. Both in units of block fraction. */
const ANCHOR_DRIFT = 1.0
const ANCHOR_WEAK = 0.15
const ANCHOR_SKIP = 0.15

/** A frame this quiet (relative to the file's median loudness) counts as
 * silence rather than a dip between two words.
 *
 * Exported because chunking (lib/audioChunks.ts) looks for a pause to cut a
 * long file on, and "what counts as a pause" has to mean one thing across
 * the project — these two numbers were tuned against the reference
 * transcriptions and there is no reason for a second opinion elsewhere. */
export const SILENCE_LEVEL = 0.25
/** …and this many quiet frames in a row make a real break in the speech. */
export const MIN_PAUSE_FRAMES = 15 // 150 ms
/** Half-width of the neighbourhood a frame must be the minimum of, in frames. */
const MINIMUM_WINDOW = 12 // ±120 ms
/** Cap on candidates per block — keeps the O(n·C²) search bounded. */
const MAX_CANDIDATES = 240
/** How close a repeat has to be to count as the engine stuttering rather than
 * the same words genuinely said again later. */
const REPEAT_GAP = 1.5

/** Never chop below this, however slow the speech: a 30-second block holding
 * three words has no boundaries worth finding, and slicing it into 2-character
 * cues would be worse than leaving it long. */
const MIN_SPLIT_CHARS = 24

// ── Step 1: where the text can be cut ──

/** Sentence end, including a closing quote or bracket. Cut after the space. */
const SENTENCE_END = /[.!?…]["'”’)\]]*\s+/g
/** A comma introducing a subordinate clause — the strongest cut inside a
 * sentence. The list covers Russian and English; extend it per language. */
const CLAUSE_COMMA =
  /,\s+(?:что|чтобы|котор[а-яё]+|когда|пока|если|но|а|хотя|that|which|when|because|but|and|so)(?=[\s,.!?…]|$)/giu
/** Dash used as a separator. The dash starts the next line — that is how a
 * change of speaker reads in subtitles. */
const DASH = /\s+(?=[—–]\s)/g
const ANY_COMMA = /,\s+/g

/** Offsets at which `text` may be cut, for one level of granularity. */
function cutPoints(text: string, level: number): number[] {
  const points: number[] = []
  const push = (at: number) => {
    if (at > 0 && at < text.length) points.push(at)
  }
  if (level === 0) {
    for (const m of text.matchAll(SENTENCE_END)) push(m.index + m[0].length)
  } else if (level === 1) {
    // The comma belongs to the line it closes
    for (const m of text.matchAll(CLAUSE_COMMA)) push(m.index + 1)
  } else if (level === 2) {
    for (const m of text.matchAll(DASH)) push(m.index)
  } else if (level === 3) {
    for (const m of text.matchAll(ANY_COMMA)) push(m.index + 1)
  } else {
    // Last resort: any word gap
    for (const m of text.matchAll(/\s+/g)) push(m.index)
  }
  return points
}

const LEVELS = 5

/** A cut and how strong the boundary it sits on is: 1 for a sentence end,
 * down to 0.2 for a bare word gap. Used to match cuts to pauses — a sentence
 * usually ends on a longer silence than a comma. */
interface Split {
  pieces: string[]
  /** strength of the cut BEFORE pieces[i + 1] */
  strengths: number[]
}

const LEVEL_STRENGTH = [1, 0.8, 0.7, 0.6, 0.2]

/** Cut `text` into pieces of at most `limit` characters, taking the strongest
 * boundary available and descending a level only while a piece is still too
 * long. The cut nearest the middle wins, so pieces stay even. */
function splitText(text: string, limit: number): Split {
  const trimmed = text.trim()
  if (trimmed.length <= limit) return { pieces: [trimmed], strengths: [] }
  const middle = trimmed.length / 2
  for (let level = 0; level < LEVELS; level++) {
    const points = cutPoints(trimmed, level)
    if (points.length === 0) continue
    let best = points[0]
    for (const p of points) if (Math.abs(p - middle) < Math.abs(best - middle)) best = p
    const left = trimmed.slice(0, best).trim()
    const right = trimmed.slice(best).trim()
    if (!left || !right) continue
    const a = splitText(left, limit)
    const b = splitText(right, limit)
    return {
      pieces: [...a.pieces, ...b.pieces],
      strengths: [...a.strengths, LEVEL_STRENGTH[level], ...b.strengths],
    }
  }
  return { pieces: [trimmed], strengths: [] }
}

// ── Step 2: where the boundaries land in time ──

/**
 * The map from "how far into the text" to "when in the block", anchored on the
 * audio.
 *
 * Plain proportion assumes the speaker keeps one pace and never stops, and a
 * block holding four sentences breaks that badly enough to shift a line by a
 * second. But a sentence end leaves a mark in the audio — a pause — and those
 * are visible in the envelope. Matching the sentence ends of the block to its
 * pauses, in order, pins the map at those points; between them it stays
 * proportional. Sentence ends that find nothing convincing stay unpinned.
 */
function anchoredMap(
  text: string,
  start: number,
  end: number,
  pauses: Candidate[],
  hop: number,
): (frac: number) => number {
  const span = end - start
  const linear = (frac: number) => start + span * frac
  // Sentence ends and the commas that open a clause: both leave a pause, and
  // the more of them are pinned the less room proportion has to drift
  const ends = [...cutPoints(text, 0), ...cutPoints(text, 1)].sort((a, b) => a - b)
  if (ends.length === 0 || pauses.length === 0 || span <= 0) return linear

  const fracs = ends.map((o) => o / text.length)
  const at = pauses.map((c) => (c.frame * hop - start) / span)
  const weak = pauses.map((c) => 1 - Math.min(1, c.pause / 40))

  // Monotonic assignment of sentence ends to pauses: cost[i][j] = best cost
  // for the first i sentence ends using pauses up to j.
  const k = fracs.length
  const m = pauses.length
  const cost: Float64Array[] = []
  const from: Int32Array[] = []
  for (let i = 0; i < k; i++) {
    cost.push(new Float64Array(m + 1).fill(Infinity))
    from.push(new Int32Array(m + 1).fill(-2)) // -2 = unset, -1 = unmatched
  }
  const pairCost = (i: number, j: number) =>
    ANCHOR_DRIFT * Math.abs(fracs[i] - at[j]) + ANCHOR_WEAK * weak[j]
  for (let i = 0; i < k; i++) {
    for (let j = 0; j <= m; j++) {
      // j = 0 means "no pause claimed yet"; pause index is j - 1
      const prev = i === 0 ? (j === 0 ? 0 : Infinity) : cost[i - 1][j]
      let best = prev + ANCHOR_SKIP // this sentence end stays unpinned
      let pick = -1
      for (let q = 1; q <= j; q++) {
        const before = i === 0 ? (q === 1 ? 0 : Infinity) : cost[i - 1][q - 1]
        if (before === Infinity) continue
        const c = before + pairCost(i, q - 1)
        if (c < best) {
          best = c
          pick = q - 1
        }
      }
      cost[i][j] = best
      from[i][j] = pick
    }
  }
  let bestEnd = 0
  for (let j = 1; j <= m; j++) if (cost[k - 1][j] < cost[k - 1][bestEnd]) bestEnd = j
  if (!isFinite(cost[k - 1][bestEnd])) return linear

  const anchors: { frac: number; time: number }[] = []
  let j = bestEnd
  for (let i = k - 1; i >= 0; i--) {
    const pick = from[i][j]
    if (pick >= 0) {
      anchors.push({ frac: fracs[i], time: pauses[pick].frame * hop })
      j = pick
    }
  }
  anchors.reverse()
  if (anchors.length === 0) return linear

  // Piecewise linear through the anchors, proportional between them
  const points = [{ frac: 0, time: start }, ...anchors, { frac: 1, time: end }]
  return (frac: number) => {
    for (let i = 1; i < points.length; i++) {
      if (frac > points[i].frac) continue
      const a = points[i - 1]
      const b = points[i]
      const width = b.frac - a.frac
      return width <= 0 ? a.time : a.time + ((b.time - a.time) * (frac - a.frac)) / width
    }
    return end
  }
}

function proportional(pieces: string[], start: number, end: number): number[] {
  const total = pieces.reduce((sum, p) => sum + p.length, 0) || 1
  const span = end - start
  const times: number[] = [start]
  let acc = 0
  for (let i = 0; i < pieces.length - 1; i++) {
    acc += pieces[i].length
    times.push(start + (span * acc) / total)
  }
  times.push(end)
  return times
}

/** A place the block may be cut: a frame index, and how many frames of
 * silence surround it (0 for a mere dip between words). */
interface Candidate {
  frame: number
  pause: number
}

/** The middle of every real pause in the block: a run of frames quiet enough,
 * and long enough, to be a break between phrases rather than the dip between
 * two words. Cutting anywhere else lands mid-word, which reads as the subtitle
 * being out of sync even when the timing is right to the millisecond. */
function pauseFrames(
  env: LoudnessEnvelope,
  start: number,
  end: number,
  median: number,
): Candidate[] {
  const threshold = median * SILENCE_LEVEL
  const first = Math.max(0, Math.ceil(start / env.hop))
  const last = Math.min(env.rms.length - 1, Math.floor(end / env.hop))
  const out: Candidate[] = []
  let run = 0
  for (let i = first; i <= last; i++) {
    if (env.rms[i] <= threshold) {
      run++
      continue
    }
    if (run >= MIN_PAUSE_FRAMES) out.push({ frame: i - Math.round(run / 2), pause: run })
    run = 0
  }
  if (run >= MIN_PAUSE_FRAMES) out.push({ frame: last - Math.round(run / 2), pause: run })
  return out
}

/** Frames that are the quietest point of their ±120 ms neighbourhood. Used
 * only where real pauses are too few to cut on — music or a constant noise
 * floor never crosses the silence threshold. */
function quietFrames(env: LoudnessEnvelope, start: number, end: number): Candidate[] {
  const first = Math.max(1, Math.ceil(start / env.hop))
  const last = Math.min(env.rms.length - 2, Math.floor(end / env.hop))
  const found: number[] = []
  for (let i = first; i <= last; i++) {
    const v = env.rms[i]
    let isMin = true
    for (let j = Math.max(0, i - MINIMUM_WINDOW); j <= Math.min(env.rms.length - 1, i + MINIMUM_WINDOW); j++) {
      if (env.rms[j] < v) {
        isMin = false
        break
      }
    }
    if (isMin) found.push(i)
  }
  if (found.length <= MAX_CANDIDATES) return found.map((frame) => ({ frame, pause: 0 }))
  // Too many to search exhaustively — keep the quietest ones, in time order
  const quietest = [...found].sort((a, b) => env.rms[a] - env.rms[b]).slice(0, MAX_CANDIDATES)
  return quietest.sort((a, b) => a - b).map((frame) => ({ frame, pause: 0 }))
}

function medianOf(values: Float32Array): number {
  if (values.length === 0) return 0
  const copy = Float32Array.from(values)
  copy.sort()
  return copy[copy.length >> 1] || 0
}

/** Pick the boundary set minimising `Σ (energy at the cut + drift from the
 * proportional estimate)`, subject to the duration and CPS limits. Dynamic
 * programming over the given candidate frames: O(n·C²). Returns null when no
 * feasible set exists — the caller then widens the candidates, and failing
 * that keeps the proportional timings. */
function alignToAudio(
  pieces: string[],
  strengths: number[],
  start: number,
  end: number,
  env: LoudnessEnvelope,
  envMedian: number,
  candidates: Candidate[],
  estimate: (frac: number) => number,
): number[] | null {
  const n = pieces.length
  const needed = n - 1
  if (candidates.length < needed) return null

  const span = end - start
  const total = pieces.reduce((sum, p) => sum + p.length, 0) || 1
  const frac: number[] = []
  let acc = 0
  for (let i = 0; i < needed; i++) {
    acc += pieces[i].length
    frac.push(acc / total)
  }

  const time = (c: number) => candidates[c].frame * env.hop
  /** How much of a break a candidate is: 0 for a dip between words, 1 for
   * 400 ms of silence or more. A sentence end belongs on a long pause, a
   * comma on a short one. */
  const breakStrength = (c: number) => Math.min(1, candidates[c].pause / 40)
  const fits = (from: number, to: number, piece: string) => {
    const dur = to - from
    return dur >= MIN_DUR && dur <= MAX_DUR && piece.length / dur <= MAX_CPS
  }
  const wanted = frac.map(estimate)
  const nodeCost = (c: number, i: number) =>
    W_ENERGY * (env.rms[candidates[c].frame] / (envMedian || 1)) +
    W_PRIOR * (Math.abs(time(c) - wanted[i]) / span) * n +
    W_MATCH * Math.abs(strengths[i] - breakStrength(c))

  const C = candidates.length
  const cost: Float64Array[] = []
  const from: Int32Array[] = []
  for (let i = 0; i < needed; i++) {
    cost.push(new Float64Array(C).fill(Infinity))
    from.push(new Int32Array(C).fill(-1))
  }
  for (let c = 0; c < C; c++) {
    if (fits(start, time(c), pieces[0])) cost[0][c] = nodeCost(c, 0)
  }
  for (let i = 1; i < needed; i++) {
    for (let c = 0; c < C; c++) {
      let best = Infinity
      let bestK = -1
      for (let k = 0; k < c; k++) {
        if (cost[i - 1][k] === Infinity) continue
        if (!fits(time(k), time(c), pieces[i])) continue
        if (cost[i - 1][k] < best) {
          best = cost[i - 1][k]
          bestK = k
        }
      }
      if (bestK >= 0) {
        cost[i][c] = best + nodeCost(c, i)
        from[i][c] = bestK
      }
    }
  }

  let best = Infinity
  let bestC = -1
  for (let c = 0; c < C; c++) {
    if (cost[needed - 1][c] === Infinity) continue
    if (!fits(time(c), end, pieces[n - 1])) continue
    if (cost[needed - 1][c] < best) {
      best = cost[needed - 1][c]
      bestC = c
    }
  }
  if (bestC < 0) return null

  const times = new Array<number>(n + 1)
  times[0] = start
  times[n] = end
  let c = bestC
  for (let i = needed - 1; i >= 0; i--) {
    times[i + 1] = time(c)
    c = from[i][c]
  }
  return times
}

// ── Step 3: merge back what didn't need splitting ──

/** Greedy left-to-right: keep absorbing the next piece while the result still
 * fits the norm. Without this, a block of short sentences ends up shredded. */
function mergeBack(cues: Cue[]): Cue[] {
  const out: Cue[] = []
  for (const cue of cues) {
    const last = out[out.length - 1]
    if (last) {
      const text = `${last.text} ${cue.text}`
      const dur = cue.end - last.start
      if (text.length <= MAX_CHARS && dur <= MAX_DUR && text.length / dur <= MAX_CPS) {
        out[out.length - 1] = { start: last.start, end: cue.end, text }
        continue
      }
    }
    out.push(cue)
  }
  return out
}

/** Collapse a phrase the engine transcribed more than once in a row.
 *
 * The gateway sometimes emits the same sentence as two or three consecutive
 * blocks, each with its own timestamps — the transcript then reads fine but
 * the subtitles show the same line three times. A line repeated back to back,
 * with no gap, is the engine stuttering rather than anything a speaker said,
 * so the repeats fold into the first one; the cue keeps the whole span, up to
 * the duration norm. A line that genuinely comes round again later — a jingle,
 * a catchphrase — is separated by silence, and the gap is what tells them
 * apart: past REPEAT_GAP the second one stands on its own.
 */
function collapseRepeats(cues: Cue[]): Cue[] {
  const out: Cue[] = []
  for (const cue of cues) {
    const last = out[out.length - 1]
    if (last && last.text === cue.text && cue.start - last.end <= REPEAT_GAP) {
      out[out.length - 1] = { ...last, end: Math.min(cue.end, last.start + MAX_DUR) }
      continue
    }
    out.push(cue)
  }
  return out
}

// ── Entry point ──

/** Longest text a piece of this block may hold: the character norm, unless the
 * speech is slow enough that 84 characters would run past the 7-second norm. */
function charLimit(text: string, duration: number): number {
  if (duration <= 0) return MAX_CHARS
  const cps = text.length / duration
  return Math.min(MAX_CHARS, Math.max(MIN_SPLIT_CHARS, Math.floor(MAX_DUR * cps)))
}

/**
 * Re-cut reading blocks into subtitle lines.
 *
 * `envelope` is optional by design: without audio (a translation, an entry
 * reopened from history) step 2 falls back to proportional timings, which is
 * coarser but never wrong enough to block the feature.
 */
export function segmentCues(cues: Cue[], envelope?: LoudnessEnvelope | null): Cue[] {
  const env = envelope && envelope.rms.length > 0 ? envelope : null
  const envMedian = env ? medianOf(env.rms) : 0
  const out: Cue[] = []

  for (const cue of cues) {
    const duration = cue.end - cue.start
    // Already a subtitle — leave it exactly as it is
    if (cue.text.length <= MAX_CHARS && duration <= MAX_DUR) {
      out.push(cue)
      continue
    }
    const flat = cue.text.replace(/\s+/g, ' ').trim()
    const split = splitText(flat, charLimit(flat, duration))
    const pieces = split.pieces
    if (pieces.length < 2 || duration <= 0) {
      out.push(cue)
      continue
    }
    // Real pauses first: a cut anywhere else lands mid-word, and that reads as
    // being out of sync even when the millisecond is right. Only where the
    // audio offers too few of them (music, a constant noise floor) does the
    // search widen to every quiet dip, and only then to plain proportion.
    const times =
      (env ? alignedTimes(split, flat, cue.start, cue.end, env, envMedian) : null) ??
      proportional(pieces, cue.start, cue.end)
    const cut = pieces.map((text, i) => ({ start: times[i], end: times[i + 1], text }))
    out.push(...mergeBack(cut))
  }

  // Across blocks, not within one: a repeat usually spans the block boundary
  return collapseRepeats(out)
}

/** Boundary times from the audio: pauses if they can carry the whole block,
 * otherwise every quiet dip, otherwise nothing. */
function alignedTimes(
  split: Split,
  flat: string,
  start: number,
  end: number,
  env: LoudnessEnvelope,
  envMedian: number,
): number[] | null {
  const from = start + MIN_DUR
  const to = end - MIN_DUR
  const { pieces, strengths } = split
  const pauses = pauseFrames(env, from, to, envMedian)
  const estimate = anchoredMap(flat, start, end, pauses, env.hop)
  const onlyPauses = new Set(pauses.map((c) => c.frame))
  // Real pauses AND the quiet dips between words, in one search. The dips are
  // what a block with music or a constant noise floor has to offer, and the
  // cost function already prefers a pause of the right size over a dip — so
  // the dips only win where nothing better exists.
  const candidates = [...pauses, ...quietFrames(env, from, to).filter((c) => !onlyPauses.has(c.frame))]
    .sort((a, b) => a.frame - b.frame)
  return alignToAudio(pieces, strengths, start, end, env, envMedian, candidates, estimate)
}

/** True when segmenting would actually change something — the toggle is
 * pointless (and confusing) on a transcript that is already subtitle-shaped. */
export function segmentationChanges(original: Cue[], segmented: Cue[]): boolean {
  if (original.length !== segmented.length) return true
  return original.some((c, i) => c.text !== segmented[i].text)
}
